import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { IUseStepper } from './IUseStepper';
import { PlacementAgentValues } from '../../Models';

const useStepper = (props: IUseStepper) => {
	const [searchParams, _] = useSearchParams();
	const { userIdInstitution, userEmail, institutionsAPI, investorsAPI, contractAPI, isInvestor } = props;
	const [placementAgentActiveVehicles, setPlacementAgentActiveVehicles] = useState<PlacementAgentValues[]>([]);
	const [placementAgentVehicles, setPlacementAgentVehicles] = useState<PlacementAgentValues[]>([]);
	const [currentStep, setCurrentStep] = useState<number>(0);
	const [selectedPersonType, setSelectedPersonType] = useState<1|2>(1);/* FISICA 1 | JURIDICA 2 */
	const [stepsFilled, setStepsFilled] = useState<number>(0);
	const [institutionSuitabilityTestType, setInstitutionSuitabilityTestType] = useState<number | undefined>(undefined);
	const [contractSuitabilityTestType, setContractSuitabilityTestType] = useState<number | undefined>(undefined);
	// An existing contract always uses its office's configuration, even while it is loading.
	const isExistingContract = searchParams.has('ContractId') || searchParams.has('hash');
	const resolvedSuitabilityTestType = contractSuitabilityTestType
		?? (isExistingContract ? undefined : institutionSuitabilityTestType);
	const suitabilityTestType = resolvedSuitabilityTestType ?? 0;
	const isSuitabilityTestEnabled = resolvedSuitabilityTestType === undefined
		? undefined
		: resolvedSuitabilityTestType > 0;
	const [isInvestorStatusEnabled, setIsInvestorStatusEnabled] = useState<boolean | undefined>(undefined);
	const [isCRSEnabled, setIsCRSEnabled] = useState<boolean | undefined>(undefined);
	const [isFATCAEnabled, setIsFATCAEnabled] = useState<boolean | undefined>(undefined);
	const [shouldSkipBeneficiariesGreater5Step, setShouldSkipBeneficiariesGreater5Step] = useState<boolean | undefined>(undefined);
	const [isInvestorWithoutNIFEnabled, setIsInvestorWithoutNIFEnabled] = useState<boolean | undefined>(undefined);
	const [isRegistrationNumberEnabled, setIsRegistrationNumberEnabled] = useState<boolean | undefined>(undefined);
	const [contractLanguage, setContractLanguage] = useState<string>('es');
	const [vehicleId, setVehicleId] = useState<number | null>(null);
	
	useEffect(() => {
		if (!userIdInstitution) return;
		getInstitutionData(userIdInstitution);
	}, [userIdInstitution]);

  	useEffect(() => {
		if (searchParams.has('ContractId') && !isInvestor) {
			configureInitialData(Number(searchParams.get('ContractId')));
		} 
	}, []);

	useEffect(() => {
		if (!userIdInstitution || !vehicleId) return;
		setInstitutionSuitabilityTestType(undefined);
		setIsInvestorStatusEnabled(undefined);
		setIsCRSEnabled(undefined);
		setIsFATCAEnabled(undefined);
		setShouldSkipBeneficiariesGreater5Step(undefined);
		setIsInvestorWithoutNIFEnabled(undefined);
		setIsRegistrationNumberEnabled(undefined);
		getSectionsEnabled();
	}, [userIdInstitution, vehicleId]);

	useEffect(() => {
		if (currentStep === 1 && isSuitabilityTestEnabled === false) {
			setCurrentStep(2);
			setStepsFilled((previous) => Math.max(previous, 2));
		}
	}, [currentStep, isSuitabilityTestEnabled]);


	const configureInitialData = (ContractId: number) => {
		contractAPI.getContract(ContractId).then(async(contractResponse: any) => {
			if (contractResponse.success) {
				const contract = contractResponse.data;
				if (contract.idVehicle) {
					setVehicleId(contract.idVehicle);
				}
				if (typeof contract.suitabilityTestType === "number") {
					setContractSuitabilityTestType(contract.suitabilityTestType);
				}
				if (contract.idLanguage){
					setContractLanguage(contract.idLanguage === 1 ? 'es' : 'en');
				}
				if (contract.idPersonType) {
					setSelectedPersonType(contract.idPersonType);
				}
			}
		});
	};

	const getSectionsEnabled = async () => {
		const toOptionalBoolean = (value: any): boolean | undefined => (
			typeof value === 'boolean' ? value : undefined
		);

		const toShouldSkipBeneficiariesGreater5Step = (value: any): boolean | undefined => {
			if (typeof value !== 'boolean') {
				return undefined;
			}

			// IncludesBeneficiariesGreater5=false => hide/skip the ">=5%" beneficiaries step.
			return !value;
		};

		const suitabilityTestConfigurationPromise = isExistingContract || contractSuitabilityTestType !== undefined
			? Promise.resolve(undefined)
			: institutionsAPI.getSuitabilityTestConfiguration(userIdInstitution!, vehicleId!);

		const [
			investorWithoutNIFEnabled,
			suitabilityTestConfiguration,
			investorStatusEnabled,
			fatcaEnabled,
			crsEnabled,
			includesBeneficiariesGreater5BBEnabled,
			includesRegistrationNumberEnabled,
		] = await Promise.all([
			institutionsAPI.getInvestorWithoutNIFEnabled(userIdInstitution!, vehicleId!),
			suitabilityTestConfigurationPromise,
			institutionsAPI.getInvestorStatusEnabled(userIdInstitution!, vehicleId!),
			institutionsAPI.getFATCAEnabled(userIdInstitution!, vehicleId!),
			institutionsAPI.getCRSEnabled(userIdInstitution!, vehicleId!),
			institutionsAPI.getIncludesBeneficiariesGreater5BBEnabled(userIdInstitution!, vehicleId!),
			institutionsAPI.getIncludesRegistrationNumberEnabled(userIdInstitution!, vehicleId!),
		]);

		setIsInvestorWithoutNIFEnabled(toOptionalBoolean(investorWithoutNIFEnabled));
		if (typeof suitabilityTestConfiguration?.enabled === 'boolean') {
			setInstitutionSuitabilityTestType(suitabilityTestConfiguration.enabled
				? suitabilityTestConfiguration.type ?? 1
				: 0);
		}
		setIsInvestorStatusEnabled(toOptionalBoolean(investorStatusEnabled));
		setIsFATCAEnabled(toOptionalBoolean(fatcaEnabled));
		setIsCRSEnabled(toOptionalBoolean(crsEnabled));
		setShouldSkipBeneficiariesGreater5Step(toShouldSkipBeneficiariesGreater5Step(includesBeneficiariesGreater5BBEnabled));
		setIsRegistrationNumberEnabled(toOptionalBoolean(includesRegistrationNumberEnabled));
	};

    const getInstitutionData = async (idInstitution: number) => {
		await institutionsAPI.getVehiclesByInstitution(idInstitution)
			.then((res: any) => {
				if (res.success) {
					const vehicles = res.data.map((vehicle: any) => {
						if (vehicle.idCurrency !== 49) {
							return { ...vehicle, vehicleLegalName: `${vehicle.vehicleLegalName} (USD)` };
						}

						return vehicle;
					});
					setPlacementAgentVehicles(vehicles);
					setPlacementAgentActiveVehicles(vehicles.filter((vehicle: any) => vehicle.active));
				}
			});
	}

	const onChange = (value: number) => {
		setCurrentStep(value);
	};

	const moveToStep = (step: number) => {
		setCurrentStep(step);
		setStepsFilled((prevState: number) => Math.max(prevState, step));
	};

	const moveForward = (isStepEnabled: boolean, disabledOffset = 2) => {
		const offset = isStepEnabled ? 1 : disabledOffset;
		moveToStep(currentStep + offset);
	};

	const unlockNextStep = async (contractId: number) => {
		let nextVehicleId = vehicleId;

		if (!nextVehicleId) {
			const json = await investorsAPI?.getInvestors(contractId);
			nextVehicleId = json?.data?.[0]?.idVehicle;
			if (nextVehicleId) {
				setVehicleId(nextVehicleId);
			}
		}

		if (nextVehicleId) {
			const contractResponse = await contractAPI.getContract(contractId);
			if (!contractResponse?.success || typeof contractResponse.data?.suitabilityTestType !== "number") {
				return;
			}
			const nextSuitabilityTestType = contractResponse.data.suitabilityTestType;
			setContractSuitabilityTestType(nextSuitabilityTestType);

			await checkStepsEnabled(nextVehicleId, nextSuitabilityTestType);
		}
	};

	const checkStepsEnabled = async (currentVehicleId: number, nextSuitabilityTestType: number) => {
		// Use the API response directly: React state still belongs to the previous render here.
		if (currentStep === 0) {
			moveForward(nextSuitabilityTestType > 0, 2);
			return;
		}

		if (!institutionsAPI || !userIdInstitution || !currentVehicleId) {
			moveForward(true);
			return;
		}

		const resolveShouldSkipBeneficiariesGreater5Step = async () => {
			if (typeof shouldSkipBeneficiariesGreater5Step === 'boolean') {
				return shouldSkipBeneficiariesGreater5Step;
			}

			const includesBeneficiariesGreater5 = await institutionsAPI.getIncludesBeneficiariesGreater5BBEnabled(userIdInstitution, currentVehicleId);
			const normalizedShouldSkip = includesBeneficiariesGreater5 === false;
			setShouldSkipBeneficiariesGreater5Step(normalizedShouldSkip);
			return normalizedShouldSkip;
		};

		if (selectedPersonType === 1 && currentStep === 4) {
			const isEnabled = await institutionsAPI.getInvestorStatusEnabled(userIdInstitution, currentVehicleId);
			setIsInvestorStatusEnabled(isEnabled);
			moveForward(isEnabled, 2);
			return;
		}

		if (selectedPersonType === 2 && currentStep === 5) {
			const fatcaEnabled = await institutionsAPI.getFATCAEnabled(userIdInstitution, currentVehicleId);
			setIsFATCAEnabled(fatcaEnabled);

			if (fatcaEnabled) {
				moveForward(true);
				return;
			}

			const crsEnabled = await institutionsAPI.getCRSEnabled(userIdInstitution, currentVehicleId);
			setIsCRSEnabled(crsEnabled);
			if (crsEnabled) {
				moveForward(false, 2);
				return;
			}

			const shouldSkip = await resolveShouldSkipBeneficiariesGreater5Step();
			moveForward(false, shouldSkip ? 4 : 3);
			return;
		}

		if (selectedPersonType === 2 && currentStep === 6) {
			const isEnabled = await institutionsAPI.getCRSEnabled(userIdInstitution, currentVehicleId);
			setIsCRSEnabled(isEnabled);

			if (isEnabled) {
				moveForward(true);
				return;
			}

			const shouldSkip = await resolveShouldSkipBeneficiariesGreater5Step();
			moveForward(false, shouldSkip ? 3 : 2);
			return;
		}

		if (selectedPersonType === 2 && currentStep === 7) {
			const shouldSkip = await resolveShouldSkipBeneficiariesGreater5Step();
			if (shouldSkip) {
				moveForward(false, 2);
				return;
			}

			moveForward(true);
			return;
		}

		if (selectedPersonType === 2 && currentStep === 8) {
			const shouldSkip = await resolveShouldSkipBeneficiariesGreater5Step();
			if (shouldSkip) {
				moveForward(true);
				return;
			}

			moveForward(true);
			return;
		}

		if (selectedPersonType === 2 && currentStep === 10) {
			const isEnabled = await institutionsAPI.getInvestorStatusEnabled(userIdInstitution, currentVehicleId);
			setIsInvestorStatusEnabled(isEnabled);
			moveForward(isEnabled, 2);
			return;
		}

		moveForward(true);
	};

	let blackLogo = '';
	if (userEmail.includes('santander')) {
	  	blackLogo = 'https://qualitasfunds.blob.core.windows.net/assets/image-gallery/SantanderBlack.png';
	} else if (userEmail.includes('singular')) {
	 	blackLogo = 'https://qualitasfunds.blob.core.windows.net/assets/image-gallery/SingularLogoBlack.png';
	} else if (userEmail.includes('creand')) {
	  	blackLogo = 'https://qualitasfunds.blob.core.windows.net/assets/image-gallery/CreandLogoBlack.png';
	} else {
	  	blackLogo = 'https://qualitasfunds.blob.core.windows.net/assets/image-gallery/blackLogo.png';
	}

	return {
		placementAgentVehicles,
		placementAgentActiveVehicles,
		vehicleId,
		isRegistrationNumberEnabled,
		currentStep,
		selectedPersonType,
		stepsFilled,
		isSuitabilityTestEnabled,
		suitabilityTestType,
		isInvestorStatusEnabled,
		isInvestorWithoutNIFEnabled,
		isCRSEnabled,
		isFATCAEnabled,
		shouldSkipBeneficiariesGreater5Step,
		onChange,
		unlockNextStep,
		blackLogo,
		setSelectedPersonType,
		setVehicleId,
		contractLanguage
	};
}

export default useStepper;
